import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Shortcut } from 'app/layout/common/shortcuts/shortcuts.types';
import { map, Observable, ReplaySubject, switchMap, take, tap, of } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class ShortcutsService {
    private _shortcuts: ReplaySubject<Shortcut[]> = new ReplaySubject<
        Shortcut[]
    >(1);

    // Static shortcuts data for development
    private _staticShortcuts: Shortcut[] = [
        {
            id: '1',
            label: 'Quantum Dashboard',
            description: 'View quantum computing overview',
            icon: 'heroicons_outline:cpu-chip',
            link: '/quantum/dashboard',
            useRouter: true
        },
        {
            id: '2',
            label: 'Circuit Builder',
            description: 'Create new quantum circuits',
            icon: 'heroicons_outline:plus-circle',
            link: '/quantum/circuit-builder',
            useRouter: true
        },
        {
            id: '3',
            label: 'ML Models',
            description: 'Manage quantum ML models',
            icon: 'heroicons_outline:swatch',
            link: '/ml/models',
            useRouter: true
        },
        {
            id: '4',
            label: 'Error Analysis',
            description: 'View error mitigation reports',
            icon: 'heroicons_outline:shield-check',
            link: '/tools/error-analysis',
            useRouter: true
        },
        {
            id: '5',
            label: 'Batch Jobs',
            description: 'Monitor batch processing queue',
            icon: 'heroicons_outline:queue-list',
            link: '/tools/batch-jobs',
            useRouter: true
        },
        {
            id: '6',
            label: 'Performance',
            description: 'System performance metrics',
            icon: 'heroicons_outline:chart-bar',
            link: '/reports/performance',
            useRouter: true
        },
        {
            id: '7',
            label: 'Documentation',
            description: 'API and user documentation',
            icon: 'heroicons_outline:document-text',
            link: 'https://docs.TriadMedicalAnalytics.com',
            useRouter: false
        },
        {
            id: '8',
            label: 'Settings',
            description: 'Application settings',
            icon: 'heroicons_outline:cog-6-tooth',
            link: '/settings',
            useRouter: true
        }
    ];

    /**
     * Constructor
     */
    constructor(private _httpClient: HttpClient) {}

    // -----------------------------------------------------------------------------------------------------
    // @ Accessors
    // -----------------------------------------------------------------------------------------------------

    /**
     * Getter for shortcuts
     */
    get shortcuts$(): Observable<Shortcut[]> {
        return this._shortcuts.asObservable();
    }

    // -----------------------------------------------------------------------------------------------------
    // @ Public methods
    // -----------------------------------------------------------------------------------------------------

    /**
     * Get all shortcuts
     */
    getAll(): Observable<Shortcut[]> {
        // Return static shortcuts for development
        return of([...this._staticShortcuts]).pipe(
            tap((shortcuts) => {
                this._shortcuts.next(shortcuts);
            })
        );

        // Original API call commented out for development
        /*
        return this._httpClient.get<Shortcut[]>('api/common/shortcuts').pipe(
            tap((shortcuts) => {
                this._shortcuts.next(shortcuts);
            })
        );
        */
    }

    /**
     * Create a shortcut
     *
     * @param shortcut
     */
    create(shortcut: Shortcut): Observable<Shortcut> {
        return this.shortcuts$.pipe(
            take(1),
            switchMap((shortcuts) => {
                // For development: simulate creating a shortcut
                const newShortcut: Shortcut = {
                    ...shortcut,
                    id: Date.now().toString()
                };

                // Update local shortcuts
                const updatedShortcuts = [...shortcuts, newShortcut];
                this._shortcuts.next(updatedShortcuts);
                this._staticShortcuts = updatedShortcuts;

                return of(newShortcut);

                // Original API call commented out
                /*
                return this._httpClient
                    .post<Shortcut>('api/common/shortcuts', { shortcut })
                    .pipe(
                        map((newShortcut) => {
                            this._shortcuts.next([...shortcuts, newShortcut]);
                            return newShortcut;
                        })
                    );
                */
            })
        );
    }

    /**
     * Update the shortcut
     *
     * @param id
     * @param shortcut
     */
    update(id: string, shortcut: Shortcut): Observable<Shortcut> {
        return this.shortcuts$.pipe(
            take(1),
            switchMap((shortcuts) => {
                // For development: simulate updating a shortcut
                const index = shortcuts.findIndex(item => item.id === id);
                if (index !== -1) {
                    const updatedShortcut = { ...shortcuts[index], ...shortcut };
                    shortcuts[index] = updatedShortcut;
                    this._shortcuts.next([...shortcuts]);
                    this._staticShortcuts = [...shortcuts];
                    return of(updatedShortcut);
                }
                return of(shortcut);

                // Original API call commented out
                /*
                return this._httpClient
                    .patch<Shortcut>('api/common/shortcuts', {
                        id,
                        shortcut,
                    })
                    .pipe(
                        map((updatedShortcut: Shortcut) => {
                            const index = shortcuts.findIndex(
                                (item) => item.id === id
                            );
                            shortcuts[index] = updatedShortcut;
                            this._shortcuts.next(shortcuts);
                            return updatedShortcut;
                        })
                    );
                */
            })
        );
    }

    /**
     * Delete the shortcut
     *
     * @param id
     */
    delete(id: string): Observable<boolean> {
        return this.shortcuts$.pipe(
            take(1),
            switchMap((shortcuts) => {
                // For development: simulate deleting a shortcut
                const index = shortcuts.findIndex(item => item.id === id);
                if (index !== -1) {
                    shortcuts.splice(index, 1);
                    this._shortcuts.next([...shortcuts]);
                    this._staticShortcuts = [...shortcuts];
                }
                return of(true);

                // Original API call commented out
                /*
                return this._httpClient
                    .delete<boolean>('api/common/shortcuts', { params: { id } })
                    .pipe(
                        map((isDeleted: boolean) => {
                            const index = shortcuts.findIndex(
                                (item) => item.id === id
                            );
                            shortcuts.splice(index, 1);
                            this._shortcuts.next(shortcuts);
                            return isDeleted;
                        })
                    );
                */
            })
        );
    }
}